// ─────────────────────────────────────────────────────────────────────────────
// Component catalog for the Workflow Engine (ADR-0011).
//
// Each component type declares its BADGE, CATEGORY, ports and PROPERTY SCHEMA in
// one entry. That is load-bearing: adding a component is a data addition here,
// never a code change — the palette, the step list and the property tabs all
// render from these entries.
//
// ── Vocabulary revision, 2026-08-14 ──────────────────────────────────────────
// Reworked after a design review against a proposed Method Workflow Designer.
// Three things were adopted from it, because they were better:
//
//   1. ROLE on every node (System / Analyst / Supervisor / Reviewer). This is a
//      LIMS: who performs a step drives work queues and segregation of duties.
//      The previous catalog had no notion of it at all.
//   2. DISCRETE preparation verbs — Sample Prep · Reagent · Extraction ·
//      Dilution — instead of one "Preparation" node hiding the vocabulary in a
//      `prepType` dropdown. A palette teaches; a dropdown does not.
//   3. Instrument Run · SDMS · Calculation as first-class nodes rather than
//      properties of a generic Analysis step.
//
// Two things were deliberately NOT adopted:
//
//   · QC stays TERMINAL and attached to its step. Batch QC is inserted into the
//     batch of the step it belongs to — the sample does not flow *through* it.
//     The reviewed design put "QC Evaluation" inline in the chain, which
//     conflates two different things, so both now exist: `qc` (insertion,
//     terminal) and `qceval` (an evaluation GATE the work really does pass
//     through).
//   · Module scoping is kept. The reviewed design is Prep/Analysis-shaped;
//     `mods` is what lets one engine also serve Sampling, Registration and
//     Reporting (ADR-0011 §3).
//
// Backwards compatibility: the original `prep` type is retained as "Sample
// Prep", so the 24 seeded workflows keep opening unchanged. The prep FAMILY
// (prep · extraction · dilution) is what carries a Tier.
//
// NodeType strings are a storage contract — they are written to
// dbo.AI_WorkflowNode.NodeType and read by AI_Workflow_List_SP's SubGroup CASE.
// ─────────────────────────────────────────────────────────────────────────────
import type { WorkflowModuleId } from './types';

export type FieldType = 'text' | 'number' | 'select' | 'bool';

/** Which tab of the node configuration panel a field belongs on. */
export type FieldTab = 'general' | 'rules' | 'execution';

export interface CatField {
  key: string;
  label: string;
  type: FieldType;
  options?: (string | number)[];
  required?: boolean;
  /** Defaults to 'general'. */
  tab?: FieldTab;
}

export interface CatEntry {
  type: string;
  label: string;
  /** Short badge shown on the palette and on every step row (S · SP · EX …). */
  badge: string;
  icon: string;
  colour: string;
  /** Palette section: CONTROL · PREPARATION · ANALYSIS · QUALITY · … */
  group: string;
  desc: string;
  /** Output ports. [] = terminal. More than one = a branch. */
  outs: string[];
  hasInput: boolean;
  single?: boolean;
  /** Modules this component is legal in; absent = all. */
  mods?: WorkflowModuleId[];
  /** True for the preparation family — these carry a Tier. */
  prepFamily?: boolean;
  fields: CatField[];
  defaults: Record<string, unknown>;
}

/** Who performs the step. Adopted from the reviewed design. */
export const ROLES = ['System', 'Analyst', 'Supervisor', 'Reviewer'] as const;

/** Role is the same field everywhere; only its DEFAULT differs per component,
 *  which lives in that component's `defaults` via commonDefaults(). */
const roleField: CatField =
  { key: 'role', label: 'Role', type: 'select', options: [...ROLES], required: true, tab: 'general' };

/** Every node gets Role + the two execution controls. */
const common = (): CatField[] => [
  roleField,
  { key: 'instructions', label: 'Instructions / SOP note', type: 'text', tab: 'general' },
  { key: 'estMinutes', label: 'Estimated minutes', type: 'number', tab: 'execution' },
  { key: 'blocking', label: 'Blocks the next step until complete', type: 'bool', tab: 'execution' },
];
const commonDefaults = (role: string) =>
  ({ role, instructions: '', estMinutes: 0, blocking: true });

const TIER: CatField = { key: 'tier', label: 'Tier', type: 'select', options: [1, 2, 3], required: true, tab: 'general' };
const PREP_TYPES = ['Sample Preparation', 'Extraction', 'Digestion', 'Filtration',
  'Distillation', 'TCLP Extraction', 'SPLP Extraction', 'TCLP VOC', 'SPLP VOC'];
const QC_TYPES = ['Method Blank', 'LCS', 'LCSD', 'MS', 'MSD', 'Duplicate', 'ICV', 'CCV',
  'LFB', 'LFSM', 'CCC', 'Media Control', 'LOQ'];
const REVIEW_LEVELS = ['DV-1-1 (Entry)', 'DV-1-2 (Validation)', 'DV-1-3 (Validation + Approval)'];

export const CATALOG: CatEntry[] = [
  // ══ CONTROL ═══════════════════════════════════════════════════════════════
  { type: 'start', label: 'Start', badge: 'S', icon: 'ti-player-play', colour: '#1f9159',
    group: 'Control', desc: 'Workflow entry', outs: ['out'], hasInput: false, single: true,
    fields: [
      { key: 'trigger', label: 'Trigger', type: 'select', required: true,
        options: ['On sample receipt', 'On login save', 'On schedule', 'On prep complete'] },
      { key: 'container', label: 'Default container', type: 'text' },
      ...common()],
    defaults: { trigger: 'On sample receipt', container: '', ...commonDefaults('System') } },

  { type: 'decision', label: 'Decision', badge: 'D', icon: 'ti-git-branch', colour: '#c2571f',
    group: 'Control', desc: 'Conditional routing', outs: ['yes', 'no'], hasInput: true,
    fields: [
      { key: 'field', label: 'Test this value', type: 'text', required: true, tab: 'rules' },
      { key: 'op', label: 'Operator', type: 'select', options: ['>', '>=', '<', '<=', '=', '<>'], required: true, tab: 'rules' },
      { key: 'value', label: 'Compare to', type: 'text', required: true, tab: 'rules' },
      { key: 'reeval', label: 'Re-evaluate when a new result arrives', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { field: 'Turbidity (NTU)', op: '>', value: '1.0', reeval: true, ...commonDefaults('System') } },

  { type: 'split', label: 'Aliquot Split', badge: 'AS', icon: 'ti-arrows-split', colour: '#7a4fd0',
    group: 'Control', desc: 'One prep feeds several analyses', outs: ['a', 'b', 'c'], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'aliquots', label: 'Number of aliquots', type: 'select', options: [2, 3, 4] },
      { key: 'volume', label: 'Volume each', type: 'text' },
      ...common()],
    defaults: { aliquots: 3, volume: '100 mL', ...commonDefaults('Analyst') } },

  { type: 'merge', label: 'Merge', badge: 'M', icon: 'ti-arrows-join', colour: '#7a869c',
    group: 'Control', desc: 'Rejoin parallel paths', outs: ['out'], hasInput: true,
    fields: [
      { key: 'waitForAll', label: 'Wait for every incoming path', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { waitForAll: true, ...commonDefaults('System') } },

  { type: 'end', label: 'End', badge: 'E', icon: 'ti-player-stop', colour: '#4a5568',
    group: 'Control', desc: 'Workflow completion', outs: [], hasInput: true,
    fields: [
      { key: 'deliverable', label: 'Deliverable', type: 'select', required: true,
        options: ['COA', 'EDD', 'Invoice', 'Internal report', 'Hand off to Sample Registration',
          'Hand off to Sample Preparation', 'Hand off to Analysis', 'Hand off to Reporting',
          'No deliverable'] },
      ...common()],
    defaults: { deliverable: 'COA', ...commonDefaults('System') } },

  // ══ PREPARATION ═══════════════════════════════════════════════════════════
  { type: 'prep', label: 'Sample Prep', badge: 'SP', icon: 'ti-flask', colour: '#0f8a7a',
    group: 'Preparation', desc: 'Prep operation, at a tier', outs: ['out'], hasInput: true,
    mods: ['prep'], prepFamily: true,
    fields: [
      { key: 'prepType', label: 'Preparation Type', type: 'select', options: PREP_TYPES, required: true },
      TIER,
      { key: 'prepMethod', label: 'Prep Method / SOP', type: 'text' },
      { key: 'equipment', label: 'Equipment Class', type: 'text' },
      { key: 'aliquot', label: 'Aliquot', type: 'text' },
      { key: 'holdHours', label: 'Hold time (h)', type: 'number', tab: 'execution' },
      ...common()],
    defaults: { prepType: 'Sample Preparation', tier: 1, prepMethod: '', equipment: '',
      aliquot: '', holdHours: 24, ...commonDefaults('Analyst') } },

  { type: 'reagent', label: 'Reagent', badge: 'R', icon: 'ti-droplet', colour: '#0f8a7a',
    group: 'Preparation', desc: 'Add / consume reagent or standard', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'reagent', label: 'Reagent / Standard', type: 'text', required: true },
      { key: 'amount', label: 'Amount', type: 'text' },
      { key: 'lotTracked', label: 'Record lot + expiry', type: 'bool', tab: 'rules' },
      { key: 'blockExpired', label: 'Block if expired', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { reagent: '', amount: '', lotTracked: true, blockExpired: true,
      ...commonDefaults('Analyst') } },

  { type: 'extraction', label: 'Extraction', badge: 'EX', icon: 'ti-filter', colour: '#0f8a7a',
    group: 'Preparation', desc: 'Extraction activity', outs: ['out'], hasInput: true,
    mods: ['prep'], prepFamily: true,
    fields: [
      { key: 'prepType', label: 'Extraction Type', type: 'select', required: true,
        options: ['Extraction', 'TCLP Extraction', 'SPLP Extraction', 'TCLP VOC', 'SPLP VOC'] },
      TIER,
      { key: 'prepMethod', label: 'Method / SOP', type: 'text' },
      { key: 'equipment', label: 'Equipment Class', type: 'text' },
      { key: 'aliquot', label: 'Aliquot', type: 'text' },
      { key: 'holdHours', label: 'Hold time (h)', type: 'number', tab: 'execution' },
      ...common()],
    defaults: { prepType: 'Extraction', tier: 1, prepMethod: '', equipment: '', aliquot: '',
      holdHours: 336, ...commonDefaults('Analyst') } },

  { type: 'dilution', label: 'Dilution', badge: 'DL', icon: 'ti-flask-2', colour: '#0f8a7a',
    group: 'Preparation', desc: 'Volume / dilution-factor step', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis'], prepFamily: true,
    fields: [
      { key: 'prepType', label: 'Preparation Type', type: 'select', options: PREP_TYPES },
      TIER,
      { key: 'factor', label: 'Dilution factor', type: 'text', required: true },
      { key: 'finalVolume', label: 'Final volume', type: 'text' },
      { key: 'autoOnOverRange', label: 'Apply automatically when over range', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { prepType: 'Sample Preparation', tier: 1, factor: '1:10', finalVolume: '',
      autoOnOverRange: false, ...commonDefaults('Analyst') } },

  { type: 'hold', label: 'Hold / Incubate', badge: 'H', icon: 'ti-clock-hour-4', colour: '#b0455f',
    group: 'Preparation', desc: 'A timed window before the next step', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'minHours', label: 'Min hours', type: 'number', required: true },
      { key: 'maxHours', label: 'Max hours', type: 'number', required: true },
      { key: 'temperature', label: 'Temperature', type: 'text' },
      { key: 'alertOnOverdue', label: 'Alert when the window closes', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { minHours: 18, maxHours: 22, temperature: '35 C', alertOnOverdue: true,
      ...commonDefaults('System') } },

  // ══ ANALYSIS ══════════════════════════════════════════════════════════════
  { type: 'analysis', label: 'Instrument Run', badge: 'IN', icon: 'ti-microscope', colour: '#c2571f',
    group: 'Analysis', desc: 'Instrument execution', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'method', label: 'Analytical Method', type: 'text', required: true },
      { key: 'instrument', label: 'Instrument Class', type: 'text' },
      { key: 'entryMode', label: 'Result Entry Mode', type: 'select', required: true,
        options: ['SDMS Entry', 'Direct Result Entry'] },
      { key: 'batchType', label: 'Batch Type', type: 'select',
        options: ['QC Batch', 'Analytical Sequence', 'Single Run'], tab: 'execution' },
      { key: 'maxBatch', label: 'Max batch size', type: 'number', tab: 'execution' },
      { key: 'calRequired', label: 'Calibration required', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { method: '', instrument: '', entryMode: 'SDMS Entry', batchType: 'QC Batch',
      maxBatch: 20, calRequired: true, ...commonDefaults('Analyst') } },

  { type: 'sdms', label: 'SDMS', badge: 'SD', icon: 'ti-file-import', colour: '#c2571f',
    group: 'Analysis', desc: 'Instrument data entry / import', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'template', label: 'SDMS Template', type: 'text', required: true },
      { key: 'source', label: 'Source', type: 'select',
        options: ['Instrument file', 'Manual entry', 'Interface'] },
      { key: 'autoImport', label: 'Import automatically when the file lands', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { template: '', source: 'Instrument file', autoImport: true,
      ...commonDefaults('Analyst') } },

  { type: 'calc', label: 'Calculation', badge: 'C', icon: 'ti-math-function', colour: '#c2571f',
    group: 'Analysis', desc: 'Formula engine step', outs: ['out'], hasInput: true,
    mods: ['prep', 'analysis', 'reporting'],
    fields: [
      { key: 'formula', label: 'Formula', type: 'text', required: true, tab: 'rules' },
      { key: 'target', label: 'Target parameter', type: 'text', required: true },
      { key: 'precision', label: 'Decimal places', type: 'number' },
      { key: 'recalcOnEdit', label: 'Recalculate when an input changes', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { formula: '', target: '', precision: 2, recalcOnEdit: true,
      ...commonDefaults('System') } },

  // ══ QUALITY ═══════════════════════════════════════════════════════════════
  { type: 'qc', label: 'QC Insertion', badge: 'QC', icon: 'ti-diamond', colour: '#7a4fd0',
    // TERMINAL by design — QC is inserted into the batch of the step it hangs
    // off; the sample does not flow through it. See the header note.
    group: 'Quality', desc: 'Inject QC into that step’s batch', outs: [], hasInput: true,
    mods: ['prep', 'analysis'],
    fields: [
      { key: 'qcType', label: 'QC Type', type: 'select', options: QC_TYPES, required: true },
      { key: 'qcRole', label: 'QCRole', type: 'text' },
      { key: 'qcRootRole', label: 'QCRootRole', type: 'text' },
      { key: 'qcSource', label: 'QC_Source', type: 'select',
        options: ['Batch-linked', 'Standard-linked', 'Source-linked'] },
      { key: 'perBatch', label: 'Per batch', type: 'number', tab: 'execution' },
      { key: 'frequency', label: 'Frequency', type: 'text', tab: 'execution' },
      { key: 'lower', label: 'Lower limit', type: 'text', tab: 'rules' },
      { key: 'upper', label: 'Upper limit', type: 'text', tab: 'rules' },
      ...common()],
    defaults: { qcType: 'Method Blank', qcRole: 'Blank', qcRootRole: 'MethodBlank',
      qcSource: 'Batch-linked', perBatch: 1, frequency: '1 per batch', lower: '', upper: '',
      ...commonDefaults('System') } },

  { type: 'qceval', label: 'QC Evaluation', badge: 'QE', icon: 'ti-checkup-list', colour: '#7a4fd0',
    // The GATE, as distinct from insertion above: work really does pass through
    // this, and it branches on whether QC met its acceptance criteria.
    group: 'Quality', desc: 'Acceptance rules — pass / fail gate',
    outs: ['pass', 'fail'], hasInput: true, mods: ['prep', 'analysis'],
    fields: [
      { key: 'scope', label: 'Evaluate', type: 'select', required: true,
        options: ['Preparation QC', 'Instrument QC', 'Batch QC', 'All QC on the batch'] },
      { key: 'onFail', label: 'On failure', type: 'select', tab: 'rules',
        options: ['Qualify results', 'Hold batch', 'Re-prep', 'Re-analyse'] },
      { key: 'qualifier', label: 'Qualifier applied', type: 'text', tab: 'rules' },
      ...common()],
    defaults: { scope: 'Preparation QC', onFail: 'Hold batch', qualifier: '',
      ...commonDefaults('System') } },

  { type: 'review', label: 'Review', badge: 'RV', icon: 'ti-checks', colour: '#2f7fd1',
    group: 'Quality', desc: 'Data review / approval level', outs: ['out'], hasInput: true,
    fields: [
      { key: 'level', label: 'Review Level', type: 'select', options: REVIEW_LEVELS, required: true },
      { key: 'differentUser', label: 'Approver must differ from validator', type: 'bool', tab: 'rules' },
      { key: 'eSignature', label: 'Requires electronic signature', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { level: 'DV-1-2 (Validation)', differentUser: true, eSignature: false,
      ...commonDefaults('Supervisor') } },

  // ══ SAMPLING MANAGEMENT ═══════════════════════════════════════════════════
  { type: 'route', label: 'Route / Visit', badge: 'RT', icon: 'ti-route', colour: '#7a4fd0',
    group: 'Sampling', desc: 'A scheduled collection round', outs: ['out'], hasInput: true,
    mods: ['sampling'],
    fields: [
      { key: 'mode', label: 'Collection Mode', type: 'select', required: true,
        options: ['Flutter (ALPACA Field)', 'Manual'] },
      { key: 'locations', label: 'Locations on route', type: 'number' },
      { key: 'gps', label: 'Require GPS capture', type: 'bool', tab: 'rules' },
      { key: 'window', label: 'Collection window', type: 'text', tab: 'execution' },
      ...common()],
    defaults: { mode: 'Flutter (ALPACA Field)', locations: 6, gps: true, window: '',
      ...commonDefaults('Analyst') } },

  { type: 'fieldmeas', label: 'Field Measurement', badge: 'FM', icon: 'ti-temperature',
    colour: '#7a4fd0', group: 'Sampling', desc: 'Readings at the point of collection',
    outs: ['out'], hasInput: true, mods: ['sampling'],
    fields: [
      { key: 'parameters', label: 'Parameters', type: 'text', required: true },
      { key: 'device', label: 'Device', type: 'text' },
      { key: 'mandatory', label: 'Block submission if missing', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { parameters: 'pH, Temp, Conductivity', device: '', mandatory: true,
      ...commonDefaults('Analyst') } },

  { type: 'collect', label: 'Collect Container', badge: 'CC', icon: 'ti-bottle', colour: '#7a4fd0',
    group: 'Sampling', desc: 'Fill and preserve containers', outs: ['out'], hasInput: true,
    mods: ['sampling'],
    fields: [
      { key: 'containers', label: 'Containers', type: 'text', required: true },
      { key: 'preservative', label: 'Preservative', type: 'text' },
      { key: 'coc', label: 'Chain of Custody required', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { containers: '', preservative: '', coc: true, ...commonDefaults('Analyst') } },

  // ══ SAMPLE REGISTRATION ═══════════════════════════════════════════════════
  { type: 'prelogin', label: 'Pre-Login', badge: 'PL', icon: 'ti-notes', colour: '#2f7fd1',
    group: 'Registration', desc: 'Samples staged before arrival', outs: ['out'], hasInput: true,
    mods: ['registration'],
    fields: [
      { key: 'source', label: 'Source', type: 'select', required: true,
        options: ['Sampling schedule', 'Client portal', 'Imported file', 'Manual'] },
      { key: 'template', label: 'Pre-Login template', type: 'text' },
      { key: 'autoTests', label: 'Auto-assign test list', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { source: 'Sampling schedule', template: '', autoTests: true,
      ...commonDefaults('Analyst') } },

  { type: 'condcheck', label: 'Condition Check', badge: 'CK', icon: 'ti-zoom-check',
    colour: '#2f7fd1', group: 'Registration', desc: 'Receipt condition / custody acceptance',
    outs: ['out'], hasInput: true, mods: ['registration'],
    fields: [
      { key: 'checks', label: 'Checks', type: 'text', required: true },
      { key: 'tempLimit', label: 'Temperature limit', type: 'text', tab: 'rules' },
      { key: 'nonconOnFail', label: 'Raise nonconformance on failure', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { checks: 'Temperature, custody seal, container integrity', tempLimit: '<= 6 C',
      nonconOnFail: true, ...commonDefaults('Analyst') } },

  { type: 'register', label: 'Register Sample', badge: 'RG', icon: 'ti-clipboard-check',
    colour: '#2f7fd1', group: 'Registration', desc: 'Mint the sample and its test list',
    outs: ['out'], hasInput: true, mods: ['registration'],
    fields: [
      { key: 'loginType', label: 'Login Type', type: 'select', required: true,
        options: ['Pre-Login', 'Regular Login'] },
      { key: 'idFormat', label: 'Sample ID format', type: 'text' },
      { key: 'labelPrint', label: 'Print labels on save', type: 'bool', tab: 'execution' },
      ...common()],
    defaults: { loginType: 'Regular Login', idFormat: 'yy-MM-NNNN-SS', labelPrint: true,
      ...commonDefaults('Analyst') } },

  // ══ REPORTING ═════════════════════════════════════════════════════════════
  { type: 'tmpl', label: 'Report Template', badge: 'RP', icon: 'ti-file-text', colour: '#b0455f',
    group: 'Reporting', desc: 'Which template renders the output', outs: ['out'], hasInput: true,
    mods: ['reporting'],
    fields: [
      { key: 'template', label: 'Template', type: 'text', required: true },
      { key: 'reportType', label: 'Report Type', type: 'select', required: true,
        options: ['COA', 'EDD', 'Invoice', 'Batch report'] },
      { key: 'grouping', label: 'Group by', type: 'select',
        options: ['Job', 'Sample', 'Project', 'Client'] },
      ...common()],
    defaults: { template: '', reportType: 'COA', grouping: 'Job', ...commonDefaults('System') } },

  { type: 'deliver', label: 'Delivery', badge: 'DV', icon: 'ti-send', colour: '#b0455f',
    group: 'Reporting', desc: 'How the deliverable reaches the client',
    outs: ['out'], hasInput: true, mods: ['reporting'],
    fields: [
      { key: 'channel', label: 'Channel', type: 'select', required: true,
        options: ['Email PDF', 'Client portal', 'EDD file', 'SFTP', 'Print'] },
      { key: 'recipients', label: 'Recipients', type: 'text' },
      { key: 'signed', label: 'Require electronic signature', type: 'bool', tab: 'rules' },
      ...common()],
    defaults: { channel: 'Email PDF', recipients: '', signed: true, ...commonDefaults('System') } },
];

export const CAT_BY_TYPE: Record<string, CatEntry> =
  Object.fromEntries(CATALOG.map(c => [c.type, c]));

/** Components legal in one module — absent `mods` means universal. */
export function catalogFor(moduleId: WorkflowModuleId | string): CatEntry[] {
  return CATALOG.filter(c => !c.mods || (c.mods as string[]).includes(moduleId));
}

/** Palette grouped into its sections, in catalog order. */
export function paletteGroups(moduleId: WorkflowModuleId | string): [string, CatEntry[]][] {
  const groups = new Map<string, CatEntry[]>();
  for (const c of catalogFor(moduleId)) {
    const list = groups.get(c.group) ?? [];
    list.push(c);
    groups.set(c.group, list);
  }
  return [...groups.entries()];
}

/** The preparation family — these carry a Tier and feed the Prep Methods view. */
export const PREP_FAMILY = CATALOG.filter(c => c.prepFamily).map(c => c.type);
export const isPrepFamily = (type: string) => PREP_FAMILY.includes(type);

/** Fields for one tab of the node configuration panel. */
export function fieldsForTab(entry: CatEntry | undefined, tab: FieldTab): CatField[] {
  return (entry?.fields ?? []).filter(f => (f.tab ?? 'general') === tab);
}
